import React from 'react';

const EMPTY_SCROLL_STATE = {
    contentWidth: 0,
    viewportWidth: 0,
    scrollLeft: 0,
    fixedLeft: 0,
    fixedWidth: 0,
};

const getMaxScrollLeft = (contentWidth: number, viewportWidth: number) => (
    Math.max(0, contentWidth - viewportWidth)
);

const isEmptyScrollState = (state: typeof EMPTY_SCROLL_STATE) => (
    state.contentWidth === EMPTY_SCROLL_STATE.contentWidth &&
    state.viewportWidth === EMPTY_SCROLL_STATE.viewportWidth &&
    state.scrollLeft === EMPTY_SCROLL_STATE.scrollLeft &&
    state.fixedLeft === EMPTY_SCROLL_STATE.fixedLeft &&
    state.fixedWidth === EMPTY_SCROLL_STATE.fixedWidth
);

export function useFixedHorizontalScrollbar(enabled: boolean) {
    const viewportRef = React.useRef<HTMLDivElement | null>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const scrollbarRef = React.useRef<HTMLDivElement | null>(null);
    const [scrollState, setScrollState] = React.useState(EMPTY_SCROLL_STATE);

    const measure = React.useCallback(() => {
        const viewport = viewportRef.current;
        const content = contentRef.current;

        if (!viewport || !content) {
            return;
        }

        const contentWidth = Math.ceil(content.scrollWidth);
        const viewportWidth = Math.ceil(viewport.clientWidth);
        const viewportRect = viewport.getBoundingClientRect();
        const fixedLeft = Math.max(0, Math.floor(viewportRect.left));
        const fixedWidth = Math.max(0, Math.min(viewportWidth, Math.ceil(window.innerWidth - fixedLeft)));

        setScrollState(prev => {
            const scrollLeft = Math.min(prev.scrollLeft, getMaxScrollLeft(contentWidth, viewportWidth));

            if (
                prev.contentWidth === contentWidth &&
                prev.viewportWidth === viewportWidth &&
                prev.scrollLeft === scrollLeft &&
                prev.fixedLeft === fixedLeft &&
                prev.fixedWidth === fixedWidth
            ) {
                return prev;
            }

            return {
                contentWidth,
                viewportWidth,
                scrollLeft,
                fixedLeft,
                fixedWidth,
            };
        });
    }, []);

    React.useEffect(() => {
        if (enabled) {
            return;
        }

        setScrollState(prev => {
            if (isEmptyScrollState(prev)) {
                return prev;
            }

            return EMPTY_SCROLL_STATE;
        });
    }, [enabled]);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        measure();

        const viewport = viewportRef.current;
        const content = contentRef.current;

        if (!viewport || !content || typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            window.addEventListener('scroll', measure, true);

            return () => {
                window.removeEventListener('resize', measure);
                window.removeEventListener('scroll', measure, true);
            };
        }

        const observer = new ResizeObserver(measure);
        observer.observe(viewport);
        observer.observe(content);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [enabled, measure]);

    React.useEffect(() => {
        const scrollbar = scrollbarRef.current;

        if (!scrollbar) {
            return;
        }

        if (Math.abs(scrollbar.scrollLeft - scrollState.scrollLeft) > 1) {
            scrollbar.scrollLeft = scrollState.scrollLeft;
        }
    }, [scrollState.scrollLeft]);

    const handleScrollbarScroll = React.useCallback(() => {
        const scrollbar = scrollbarRef.current;

        if (!scrollbar) {
            return;
        }

        setScrollState(prev => {
            const scrollLeft = Math.min(
                scrollbar.scrollLeft,
                getMaxScrollLeft(prev.contentWidth, prev.viewportWidth),
            );

            if (prev.scrollLeft === scrollLeft) {
                return prev;
            }

            return {
                ...prev,
                scrollLeft,
            };
        });
    }, []);

    const setScrollLeft = React.useCallback((nextScrollLeft: number | ((currentScrollLeft: number) => number)) => {
        setScrollState(prev => {
            const nextValue = typeof nextScrollLeft === 'function'
                ? nextScrollLeft(prev.scrollLeft)
                : nextScrollLeft;
            const scrollLeft = Math.max(
                0,
                Math.min(nextValue, getMaxScrollLeft(prev.contentWidth, prev.viewportWidth)),
            );

            if (prev.scrollLeft === scrollLeft) {
                return prev;
            }

            return {
                ...prev,
                scrollLeft,
            };
        });
    }, []);

    const showScrollbar = enabled &&
        getMaxScrollLeft(scrollState.contentWidth, scrollState.viewportWidth) > 1 &&
        scrollState.fixedWidth > 0;

    const scrollbarStyle = React.useMemo((): React.CSSProperties => ({
        left: scrollState.fixedLeft,
        width: scrollState.fixedWidth,
    }), [scrollState.fixedLeft, scrollState.fixedWidth]);

    return {
        viewportRef,
        contentRef,
        scrollbarRef,
        contentWidth: scrollState.contentWidth,
        scrollLeft: scrollState.scrollLeft,
        showScrollbar,
        scrollbarStyle,
        measure,
        handleScrollbarScroll,
        setScrollLeft,
    };
}
